import type Phaser from 'phaser';
import type { TutorialDeps, TutorialStep, TutorialStepCompleteCallback } from './types';
import {
  runStep1,
  step1Layout,
  step1OnDragStart,
  step1OnDragEnd,
  step1RestartBounce,
  destroyStep1,
  type Step1State,
} from './step1';
import {
  enterStep2,
  layoutStep2,
  handleDragEndStep2,
  destroyStep2,
  type Step2State,
} from './step2';
import {
  createTutorialWelcomeHeader,
  positionTutorialWelcomeHeader,
  destroyTutorialWelcomeHeader,
} from './ui';

export type TutorialControllerOptions = {
  deps: TutorialDeps;
  onStepComplete?: TutorialStepCompleteCallback;
};

/**
 * Контроллер туториала. Содержит логику только для шагов туториала;
 * сцена передаёт deps и вызывает start/layout/destroy.
 */
export class TutorialController {
  private scene: Phaser.Scene;
  private deps: TutorialDeps;
  private onStepComplete: TutorialStepCompleteCallback | undefined;

  private step: TutorialStep = 1;
  private step1State: Step1State | null = null;
  private step2State: Step2State | null = null;
  private welcomeHeaderEl: HTMLElement | null = null;
  private welcomeHeaderEnabled = true;
  private piecePlacedListener = (data: { pieceId: number }) => this.handlePiecePlaced(data);

  constructor(options: TutorialControllerOptions) {
    this.scene = options.deps.scene;
    this.deps = options.deps;
    this.onStepComplete = options.onStepComplete;
  }

  start(): void {
    this.step = 1;
    this.welcomeHeaderEnabled = true;
    this.welcomeHeaderEl = createTutorialWelcomeHeader();
    this.step1State = runStep1(this.deps);
    this.scene.events.on('piece:placed', this.piecePlacedListener);
    this.layoutWelcomeHeader();
  }

  /** Продолжить туториал с шага 2 (после возврата в игру, шаг 1 уже был сделан). */
  startAtStep2(): void {
    this.step = 2;
    this.welcomeHeaderEnabled = true;
    this.welcomeHeaderEl = createTutorialWelcomeHeader();
    this.step2State = enterStep2(this.deps);
    this.scene.events.on('piece:placed', this.piecePlacedListener);
    this.layoutWelcomeHeader();
  }

  layout(): void {
    // После refreshPuzzle (step 1→2) showPuzzleHud пересоздаёт DOM — заголовок может пропасть; воссоздаём.
    if (
      this.welcomeHeaderEnabled &&
      (!this.welcomeHeaderEl || !this.welcomeHeaderEl.parentNode) &&
      (this.step === 1 || this.step === 2)
    ) {
      this.welcomeHeaderEl = createTutorialWelcomeHeader();
    }
    this.layoutWelcomeHeader();
    if (this.step === 1 && this.step1State) {
      step1Layout(this.step1State, this.deps);
    }
    if (this.step === 2 && this.step2State) {
      layoutStep2(this.step2State, this.deps);
    }
  }

  private layoutWelcomeHeader(): void {
    if (!this.welcomeHeaderEnabled) return;
    if (!this.welcomeHeaderEl?.parentNode) return;
    const boardRect = this.deps.getBoardRect();
    if (!boardRect) return;
    const canvasRect = this.scene.game.canvas.getBoundingClientRect();
    const cam = this.scene.cameras.main;
    positionTutorialWelcomeHeader(this.welcomeHeaderEl!, boardRect, canvasRect, cam.width, cam.height);
  }

  destroy(): void {
    this.scene.events.off('piece:placed', this.piecePlacedListener);
    destroyTutorialWelcomeHeader(this.welcomeHeaderEl);
    this.welcomeHeaderEl = null;
    if (this.step1State) {
      destroyStep1(this.step1State, this.deps);
      this.step1State = null;
    }
    if (this.step2State) {
      destroyStep2(this.step2State);
      this.step2State = null;
    }
  }

  hideWelcomeHeader(): void {
    this.welcomeHeaderEnabled = false;
    destroyTutorialWelcomeHeader(this.welcomeHeaderEl);
    this.welcomeHeaderEl = null;
  }

  /** Текущий шаг (для сцены). */
  getStep(): TutorialStep {
    return this.step;
  }

  /** Id целевого кусочка в Step 1; в остальных шагах null. */
  getTargetPieceId(): number | null {
    if (this.step !== 1 || !this.step1State) return null;
    return this.step1State.targetPieceId;
  }

  /** Вызвать при начале перетаскивания кусочка из лотка (остановить bounce, показать подсветку слота). */
  onTrayDragStart(pieceId: number): void {
    if (this.step === 1 && this.step1State && this.step1State.targetPieceId === pieceId) {
      step1OnDragStart(this.step1State, this.deps);
    }
  }

  /** Вызвать при окончании перетаскивания (Step 1: скрыть подсветку; Step 2: скрыть подсказку при первом дропе). */
  onTrayDragEnd(): void {
    if (this.step === 1 && this.step1State) {
      step1OnDragEnd(this.step1State);
    }
    if (this.step === 2 && this.step2State) {
      handleDragEndStep2(this.step2State);
    }
  }

  /** Вызвать после возврата кусочка в лоток из-за drop вне targetRect (перезапуск пульсации). */
  onTrayPieceReturnedAfterFailedDrop(pieceId: number, img: Phaser.GameObjects.Image): void {
    if (this.step === 1 && this.step1State && this.step1State.targetPieceId === pieceId) {
      step1RestartBounce(this.step1State, this.deps.scene, img);
    }
  }

  private handlePiecePlaced(data: { pieceId: number }): void {
    if (this.step !== 1 || !this.step1State) return;
    if (data.pieceId !== this.step1State.targetPieceId) return;
    this.onStepComplete?.(1);
    this.step = 2;
    destroyStep1(this.step1State, this.deps);
    this.step1State = null;
    this.step2State = enterStep2(this.deps);
  }
}
